import { useEffect, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { defaultDateRange } from '@/lib/date-range';
import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { FilterBar } from '@/components/admin/filter-bar';
import { AuditTable } from '@/components/admin/audit/audit-table';
import { EntityTypePicker } from '@/components/admin/audit/entity-type-picker';
import { OrganizationPicker } from '@/components/admin/shared/organization-picker';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { useAdminAuditLog } from '@/hooks/data/admin/use-audit';
import { AdminAuditEntityType } from '@/types/generated/enums';
import type { AdminAuditEntityType as AdminAuditEntityTypeT } from '@/types/generated';

const searchSchema = z.object({
  entity_type: z.nativeEnum(AdminAuditEntityType).optional(),
  organization_id: z.number().int().positive().optional(),
  actor_user_id: z.number().int().positive().optional(),
  start: z.string().date().optional(),
  end: z.string().date().optional(),
  offset: z.number().int().nonnegative().default(0),
});

const LIMIT = 20;

export const Route = createFileRoute('/_authenticated/admin/audit')({
  validateSearch: searchSchema,
  staticData: { title: 'Audit log' },
  component: AdminAuditPage,
});

function AdminAuditPage(): React.ReactElement {
  const { entity_type, organization_id, actor_user_id, start, end, offset } =
    Route.useSearch();
  const navigate = Route.useNavigate();

  // Seed a sensible default window once so the date filters don't load blank.
  // Only runs on mount, so the user can still clear the dates afterwards.
  const defaultRange = useMemo(() => defaultDateRange(7), []);
  useEffect(() => {
    if (start === undefined && end === undefined) {
      void navigate({
        search: (s) => ({
          ...s,
          start: defaultRange.start,
          end: defaultRange.end,
        }),
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const auditQuery = useAdminAuditLog({
    entity_type,
    organization_id,
    actor_user_id,
    start,
    end,
    limit: LIMIT,
    offset,
  });

  const setEntityType = (value: AdminAuditEntityTypeT | undefined): void => {
    void navigate({
      search: (s) => ({ ...s, entity_type: value, offset: 0 }),
    });
  };

  const setOrg = (value: number | undefined): void => {
    void navigate({
      search: (s) => ({ ...s, organization_id: value, offset: 0 }),
    });
  };

  const setActorUserId = (value: string): void => {
    const parsed = value ? parseInt(value, 10) : undefined;
    void navigate({
      search: (s) => ({
        ...s,
        actor_user_id: parsed && !Number.isNaN(parsed) ? parsed : undefined,
        offset: 0,
      }),
    });
  };

  const setStart = (value: string): void => {
    void navigate({
      search: (s) => ({ ...s, start: value || undefined, offset: 0 }),
    });
  };

  const setEnd = (value: string): void => {
    void navigate({
      search: (s) => ({ ...s, end: value || undefined, offset: 0 }),
    });
  };

  return (
    <main>
      <AdminPageHeader title="Audit log" description="Cross-org audit trail" />
      <FilterBar>
        <EntityTypePicker value={entity_type} onChange={setEntityType} />
        <OrganizationPicker value={organization_id} onChange={setOrg} />
        <Input
          type="number"
          placeholder="Actor user ID"
          value={actor_user_id ?? ''}
          onChange={(e) => setActorUserId(e.target.value)}
          className="w-36"
        />
        <DatePicker
          value={start ?? undefined}
          onChange={setStart}
          max={end ?? undefined}
          className="w-40"
          ariaLabel="Start date"
        />
        <DatePicker
          value={end ?? undefined}
          onChange={setEnd}
          min={start ?? undefined}
          className="w-40"
          ariaLabel="End date"
        />
      </FilterBar>

      <AuditTable
        query={auditQuery}
        offset={offset}
        limit={LIMIT}
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
      />
    </main>
  );
}
